import { useEffect, useRef, useState } from 'react'
import { rotateNodeToken } from '@/api/secrets'

interface Props {
  nodeId: string
  nodeName: string
  onClose: () => void
}

type Phase = 'confirm' | 'rotating' | 'done' | 'error'

export default function TokenRotateModal({ nodeId, nodeName, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('confirm')
  const [token, setToken] = useState<string | null>(null)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [reason, setReason] = useState('')
  const tokenRef = useRef<string | null>(null)

  // Security: clear token from memory when modal unmounts
  useEffect(() => {
    return () => {
      tokenRef.current = null
      setToken(null)
    }
  }, [])

  async function handleRotate() {
    setPhase('rotating')
    try {
      const res = await rotateNodeToken(nodeId, { reason: reason.trim() })
      tokenRef.current = res.token
      setToken(res.token)
      setPhase('done')
    } catch (e: unknown) {
      const err = e as { message?: string }
      setErrMsg(err?.message ?? 'Token rotation failed')
      setPhase('error')
    }
  }

  function handleCopy() {
    if (!token) return
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleClose() {
    // Clear token from state before closing
    tokenRef.current = null
    setToken(null)
    onClose()
  }

  return (
    <div style={backdropStyle} onClick={(e) => { if (e.target === e.currentTarget && phase !== 'rotating') handleClose() }}>
      <div style={modalStyle} role="dialog" aria-modal="true">
        <div style={headerStyle}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Rotate Node Token</span>
          {phase !== 'rotating' && (
            <button onClick={handleClose} style={closeBtnStyle} aria-label="Close">x</button>
          )}
        </div>

        {phase === 'confirm' && (
          <>
            <div style={warningBoxStyle}>
              <strong>Warning:</strong> Rotating the token will immediately invalidate the current
              token for node <code style={{ fontFamily: 'monospace' }}>{nodeName}</code>.
              The node agent will be unable to communicate with the server until the new token
              is installed on the node.
            </div>
            <p style={{ fontSize: 13, color: '#444', marginBottom: 20 }}>
              Are you sure you want to rotate the token for <strong>{nodeName}</strong>?
            </p>
            <label style={{ display: 'block', marginBottom: 20, fontSize: 12, color: '#444' }}>
              Reason
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                minLength={3}
                maxLength={500}
                required
                placeholder="Lost token, node reinstall, suspected exposure, etc."
                style={{
                  boxSizing: 'border-box',
                  width: '100%',
                  minHeight: 72,
                  marginTop: 6,
                  padding: '8px 10px',
                  resize: 'vertical',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  fontFamily: 'inherit',
                  fontSize: 12,
                }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={handleClose} style={secondaryBtnStyle}>Cancel</button>
              <button
                onClick={handleRotate}
                disabled={reason.trim().length < 3}
                style={{
                  ...dangerBtnStyle,
                  opacity: reason.trim().length < 3 ? 0.5 : 1,
                  cursor: reason.trim().length < 3 ? 'not-allowed' : 'pointer',
                }}
              >
                Rotate token
              </button>
            </div>
          </>
        )}

        {phase === 'rotating' && (
          <p style={{ color: '#888', fontSize: 13, margin: '16px 0' }}>Rotating token...</p>
        )}

        {phase === 'done' && token && (
          <>
            <div style={{ ...warningBoxStyle, background: '#e8f5e9', border: '1px solid #a5d6a7', color: '#1b5e20' }}>
              Token rotated successfully. Copy this token now — it will not be shown again once you close this dialog.
            </div>
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>
                New Token (one-time display)
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  readOnly
                  value={token}
                  style={{
                    flex: 1,
                    padding: '7px 10px',
                    border: '1px solid #d0d0d0',
                    borderRadius: 4,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    background: '#f8f8fa',
                    color: '#222',
                  }}
                  onFocus={(e) => e.target.select()}
                />
                <button onClick={handleCopy} style={primaryBtnStyle}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#c62828', marginBottom: 20 }}>
              Store this token securely. After closing, it cannot be retrieved.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleClose} style={primaryBtnStyle}>Close</button>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <div style={warningBoxStyle}>{errMsg}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={handleClose} style={secondaryBtnStyle}>Close</button>
              <button onClick={() => { setPhase('confirm'); setErrMsg(null) }} style={dangerBtnStyle}>
                Try again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
}

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: '20px 24px',
  width: 480,
  maxWidth: '95vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: '1px solid #e0e0e0',
}

const closeBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 16,
  color: '#888',
  padding: '2px 6px',
  fontFamily: 'inherit',
}

const warningBoxStyle: React.CSSProperties = {
  background: '#fff8e1',
  border: '1px solid #ffd54f',
  borderRadius: 4,
  padding: '10px 14px',
  fontSize: 13,
  color: '#5d4037',
  marginBottom: 16,
  lineHeight: 1.5,
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const dangerBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#c62828',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
